/* eslint-disable react-hooks/exhaustive-deps */
import React, { useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import moment from 'moment-timezone';
import { get } from 'lodash';
import { injectIntl, intlShape } from 'react-intl';
import { func, arrayOf, bool } from 'prop-types';
import { dateObjectToJson, daysAdded } from '../../util/dates';
import { Page, Container, InquiryWizard } from '../../components';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { getListingState, getListingTimezone, getLodgingPricesOptions } from '../../util/listing';
import { loadData, sendEnquiry, setInitialValues } from './NewInquiryPage.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { ensureListing } from '../../util/data';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createResourceLocatorString } from '../../util/routes';
import routeConfiguration from '../../routeConfiguration';
import { propTypes } from '../../util/types';
import {
  getPackageLodgingPrices,
  calculateFlexiblePricing,
  isFlexiblePricingEnabled,
} from '../../util/package';
import { commissionRateMap } from '../../marketplace-custom-config';
import { followSubject } from '../../ducks/user.duck';
import {
  INQUIRY_BLOCK_MESSAGE,
  INQUIRY_BLOCK_SESSION,
  INQUIRY_BLOCK_SIGN_IN,
  INQUIRY_BLOCK_UNVERIFIED,
  endInquirySubmit,
  inquirySubmitBlock,
  tryBeginInquirySubmit,
} from '../../components/InquiryWizard/inquiryAccess';
import { INQUIRY_MESSAGE_MAX_LENGTH } from '../../components/InquiryWizard/inquiryPackageModel';
import { clearInquiryDraft } from '../../components/InquiryWizard/inquiryDraft';
import css from './NewInquiryPage.module.css';
import * as log from '../../util/log';
const { PROVIDER_COMMISSION_PERCENTAGE } = require('../../shared/transaction');

const { UUID } = sdkTypes;
const USER_PENDING_APPROVAL_ERROR_CODE = 'user-pending-approval';
const USER_EMAIL_UNVERIFIED_ERROR_CODE = 'user-email-unverified';
const AUTHENTICATION_REQUIRED_ERROR_CODE = 'authentication-required';
const INQUIRY_NOT_CREATED_ERROR_CODE = 'inquiry-not-created';

const NewInquiryPageComponent = ({
  intl,
  history,
  location,
  params,
  getListing,
  timeSlots,
  sendEnquiryInProgress,
  onSendInquiry,
  currentUser,
  followSubjectMutation,
  scrollingDisabled,
}) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const [submitInFlight, setSubmitInFlight] = useState(false);
  const [submitAttempt, setSubmitAttempt] = useState(0);
  const submitLockRef = useRef(false);
  const currentUserRef = useRef(currentUser);
  currentUserRef.current = currentUser;

  const releaseSubmit = () => {
    endInquirySubmit(submitLockRef);
    setSubmitInFlight(false);
    setSubmitAttempt(attempt => attempt + 1);
  };

  const searchParams = new URLSearchParams(location.search);
  const activityParam = searchParams.get('activity');

  const listing = ensureListing(getListing(new UUID(params.id)));

  if (!listing.id) return null;

  const handleSubmit = async values => {
    if (!tryBeginInquirySubmit(submitLockRef)) {
      return;
    }
    setSubmitInFlight(true);
    setErrorMessage(null);

    const stop = message => {
      setErrorMessage(message);
      releaseSubmit();
    };

    try {
      const user = currentUserRef.current;
      const block = inquirySubmitBlock(user, values?.message);
      if (block === INQUIRY_BLOCK_SIGN_IN) {
        stop(
          intl.formatMessage({
            id: 'NewInquiryPage.signInRequiredInquiryError',
          })
        );
        return;
      }
      if (block === INQUIRY_BLOCK_UNVERIFIED) {
        stop(
          intl.formatMessage({
            id: 'NewInquiryPage.emailVerificationInquiryError',
          })
        );
        return;
      }
      if (block === INQUIRY_BLOCK_SESSION) {
        stop(
          intl.formatMessage({
            id: 'NewInquiryPage.sessionExpiredInquiryError',
          })
        );
        return;
      }
      if (block === INQUIRY_BLOCK_MESSAGE) {
        stop(
          intl.formatMessage(
            { id: 'InquiryWizard.messageTooLong' },
            { maxLength: INQUIRY_MESSAGE_MAX_LENGTH }
          )
        );
        return;
      }

      const landowner = listing?.author?.id?.uuid;
      const currentUserId = user?.id?.uuid;

      if (currentUserId === landowner) {
        stop('You cannot send an inquiry for your own property.');
        return;
      }
      const { bookingDates, lodgingAddons, message, partySize, selectedPackage } = values;
      const inquiryMessage =
        typeof message === 'string' ? message.slice(0, INQUIRY_MESSAGE_MAX_LENGTH) : '';
      if (!inquiryMessage.trim()) {
        stop(intl.formatMessage({ id: 'InquiryWizard.messageRequired' }));
        return;
      }
      if (!bookingDates?.startDate || !bookingDates?.endDate) {
        stop(intl.formatMessage({ id: 'InquiryWizard.fieldRequired' }));
        return;
      }
      const packageActivity = get(selectedPackage, 'activity', null);
      const packageAmount = get(selectedPackage, 'price.amount', 0);
      const packageId = get(selectedPackage, 'id', null);
      const listingPackages = get(listing, 'attributes.publicData.packages', []);
      const listingPackage = packageId ? listingPackages.find(item => item.id === packageId) : null;

      if (packageId && (!listingPackage || !listingPackage.isPublished)) {
        stop('This package is no longer available.');
        return;
      }
      const packageDescription = get(selectedPackage, 'description', null);
      const packageTitle = get(selectedPackage, 'title', null);
      const packagePrice = {
        amount: get(selectedPackage, 'price.amount', null),
        currency: 'USD',
      };
      const packageSpecies = get(selectedPackage, 'species', []);

      const { startDate, endDate, startLodgingDate } = bookingDates;

      const days = daysAdded(startDate, endDate);
      const nights = days - 1;

      const lodgingPricesOptions = getLodgingPricesOptions(listing, intl);
      const { optionalLodging, includedLodging } = getPackageLodgingPrices(
        selectedPackage,
        lodgingPricesOptions
      );

      let totalLodgingAmount = 0;
      if (lodgingAddons?.length > 0) {
        totalLodgingAmount = lodgingAddons.reduce((total, addonIndex) => {
          const lodgingItem = optionalLodging[addonIndex];
          if (lodgingItem?.price) {
            const lodgingPrice = JSON.parse(lodgingItem.price, sdkTypes.reviver);
            return total + lodgingPrice.amount * nights;
          }
          return total;
        }, 0);
      }

      if (includedLodging?.length > 0) {
        totalLodgingAmount += includedLodging.reduce((total, item) => {
          if (item?.price) {
            const lodgingPrice = JSON.parse(item.price, sdkTypes.reviver);
            return total + lodgingPrice.amount * nights;
          }
          return total;
        }, 0);
      }

      // Calculate subtotal amount with flexible pricing support
      let subtotalAmount = 0;

      if (selectedPackage) {
        const packages = get(listing, 'attributes.publicData.packages', []);
        const packageInfo = packages.find(item => item.id === selectedPackage.id);

        // Check if flexible pricing is enabled and partySize has flexible structure
        const hasFlexiblePricing = !!partySize?.flexibleGuestSize;

        if (hasFlexiblePricing && packageInfo && isFlexiblePricingEnabled(packageInfo?.activity)) {
          // Use flexible pricing calculation
          const flexiblePricingData = calculateFlexiblePricing(
            partySize?.flexibleGuestSize,
            packageInfo,
            days
          );

          // Sum up the total from all categories
          const categoryTotals = Object.values(flexiblePricingData).reduce((acc, category) => {
            return acc + (category.totalPrice?.amount || 0);
          }, 0);

          subtotalAmount = categoryTotals;
        } else {
          // Standard pricing calculation
          subtotalAmount = packageAmount * partySize * days;
        }

        // Add lodging amount
        subtotalAmount += totalLodgingAmount;
      }

      const landownerRelationship = get(
        listing,
        'author.attributes.profile.publicData.landownerRelationship',
        'default'
      );
      const commissionRate = commissionRateMap.find(object => {
        return object.key === landownerRelationship;
      });
      const percentageSubtotal =
        ((commissionRate?.commission || PROVIDER_COMMISSION_PERCENTAGE) / 100) * subtotalAmount;

      const potentialEarnings = (subtotalAmount + percentageSubtotal) / 100;

      const startObj = dateObjectToJson(startDate);
      const endObj = dateObjectToJson(endDate);
      const endDateMaybe = startDate.toISOString() !== endDate.toISOString() ? endObj : null;

      // We wanna get the date at the listing location.
      // This will then be used for the display dates which are hooked
      // with our process.end transitions
      const timezone = getListingTimezone(listing);

      const bookingStartDate = moment(startDate).format('YYYY-MM-DD');
      const bookingEndDate = moment(endDate).format('YYYY-MM-DD');

      const bookingStartDateInLocale = moment.tz(`${bookingStartDate} 00:00:00`, timezone);
      const bookingEndDateInLocale = moment.tz(`${bookingEndDate} 23:59:59`, timezone);

      const bookingStartDateInLocaleInUTC = moment(bookingStartDateInLocale).utc();
      const bookingEndDateInLocaleInUTC = moment(bookingEndDateInLocale).utc();

      const bookingDisplayStart = bookingStartDateInLocaleInUTC.toDate();
      const bookingDisplayEnd = bookingEndDateInLocaleInUTC.toDate();

      // The API booking end date is always exclusive. In order to have e.g.
      // 1st of Jul - 2nd of July we need to send end date as 3rd.
      // Important, the 3rd of July won't be blocked from future bookings.
      const apiBookingEnd = moment
        .utc(endDate)
        .set({ hour: 0, minute: 0, seconds: 0 })
        .add(1, 'd')
        .toDate();
      const apiBookingStart = moment
        .utc(startDate)
        .set({ hour: 0, minute: 0 })
        .toDate();

      const txId = await onSendInquiry(listing.id.uuid, inquiryMessage, {
        species: packageSpecies,
        partySize,
        selectedPackage: selectedPackage && {
          packageId,
          packageDescription,
          packageTitle,
          packagePrice,
          packageParty: partySize,
          packageActivity,
        },
        potentialEarnings: { amount: potentialEarnings, currency: 'USD' },
        emailStart: startObj,
        emailEnd: endDateMaybe,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        emailStartLodgingDate: startLodgingDate ? dateObjectToJson(startLodgingDate) : null,
        lodgingAddons,
        activity: activityParam,
        listingUSState: getListingState(listing)?.state,
        bookingStart: apiBookingStart,
        bookingEnd: apiBookingEnd,
        bookingDisplayStart,
        bookingDisplayEnd,
      });

      clearInquiryDraft(params.id);
      history.push({
        pathname: createResourceLocatorString(
          'InboxV2Page',
          routeConfiguration(),
          { id: txId.uuid, tab: 'orders' },
          {}
        ),
        state: { showInquiryModal: true },
      });
    } catch (err) {
      releaseSubmit();
      const inquiryErrorCode = err?.data?.errors?.[0]?.code;
      if (inquiryErrorCode === USER_PENDING_APPROVAL_ERROR_CODE) {
        setErrorMessage(
          intl.formatMessage({
            id: 'NewInquiryPage.pendingApprovalInquiryError',
            defaultMessage:
              'Your account is pending approval and cannot send inquiries yet. If you have questions, contact the LandTrust team.',
          })
        );
        return;
      }

      if (inquiryErrorCode === USER_EMAIL_UNVERIFIED_ERROR_CODE) {
        setErrorMessage(
          intl.formatMessage({
            id: 'NewInquiryPage.emailVerificationInquiryError',
            defaultMessage:
              'Please verify your email address before sending inquiries. If you need help, contact the LandTrust team.',
          })
        );
        return;
      }

      if (inquiryErrorCode === AUTHENTICATION_REQUIRED_ERROR_CODE) {
        setErrorMessage(
          intl.formatMessage({
            id: 'NewInquiryPage.sessionExpiredInquiryError',
            defaultMessage: 'Your session expired. Refresh the page and send the inquiry again.',
          })
        );
        return;
      }

      // Log the error to Sentry
      log.error(
        err,
        inquiryErrorCode === INQUIRY_NOT_CREATED_ERROR_CODE
          ? INQUIRY_NOT_CREATED_ERROR_CODE
          : 'new-inquiry-submit-failed',
        {
          listingId: listing?.id?.uuid,
          values,
          currentUser: currentUserRef.current?.id?.uuid,
        }
      );

      setErrorMessage(
        intl.formatMessage({
          id: 'NewInquiryPage.genericInquiryError',
          defaultMessage:
            "We couldn't send your inquiry. Please try again. If the problem continues, contact the LandTrust team.",
        })
      );
    }
  };

  return (
    <Page
      title={intl.formatMessage({ id: 'NewInquiryPage.title' })}
      scrollingDisabled={scrollingDisabled}
      noindex
      nofollow
    >
      <Container className={css.container} isFullWidth>
        <div className={css.formContainer}>
          {listing.id && (
            <InquiryWizard
              listing={listing}
              timeSlots={timeSlots}
              sendEnquiryInProgress={sendEnquiryInProgress || submitInFlight}
              isInquirySubmitLocked={() => submitLockRef.current}
              inquirySubmitAttempt={submitAttempt}
              onSubmit={handleSubmit}
              currentUser={currentUser}
              followSubjectMutation={followSubjectMutation}
              errorMessage={errorMessage}
            />
          )}
        </div>
      </Container>
    </Page>
  );
};

NewInquiryPageComponent.defaultProps = {
  timeSlots: null,
  currentUser: null,
};

NewInquiryPageComponent.propTypes = {
  intl: intlShape.isRequired,
  getListing: func.isRequired,
  timeSlots: arrayOf(propTypes.timeSlot),

  sendEnquiryInProgress: bool.isRequired,
  onSendInquiry: func.isRequired,
  currentUser: propTypes.currentUser,
};

const mapStateToProps = state => {
  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const { timeSlots, sendEnquiryInProgress } = state.NewInquiryPage;
  const { currentUser } = state.user;

  return {
    currentUser,
    getListing,
    timeSlots,
    sendEnquiryInProgress,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onSendInquiry: (listingId, message, meta) => dispatch(sendEnquiry(listingId, message, meta)),
  followSubjectMutation: (userId, subjectId, notifyBy) =>
    dispatch(followSubject(userId, subjectId, notifyBy)),
});

const NewInquiryPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(NewInquiryPageComponent);

NewInquiryPage.setInitialValues = initialValues => setInitialValues(initialValues);
NewInquiryPage.loadData = loadData;

export default NewInquiryPage;
